/**
 * FlareHair Theme - Global JavaScript
 * Handles mobile menu, keyboard accessibility, quantity selectors,
 * tabs, product gallery, variant options, modals, announcement bar,
 * and sticky header behavior.
 */

document.addEventListener('DOMContentLoaded', () => {
  /* --------------------------------------------------------------------------
   * 1. Mobile Menu Toggle
   * ----------------------------------------------------------------------- */
  const mobileMenuBtn = document.querySelector('.mobile-menu-btn');
  const mobileMenu = document.querySelector('.mobile-menu');
  const overlay = document.querySelector('.overlay');

  if (mobileMenuBtn && mobileMenu && overlay) {
    mobileMenuBtn.addEventListener('click', () => {
      const isOpen = mobileMenu.classList.toggle('active');
      mobileMenuBtn.classList.toggle('active');
      overlay.classList.toggle('active');
      mobileMenuBtn.setAttribute('aria-expanded', String(isOpen));
      mobileMenu.setAttribute('aria-hidden', String(!isOpen));
      overlay.setAttribute('aria-hidden', String(!isOpen));
    });

    overlay.addEventListener('click', () => {
      mobileMenuBtn.classList.remove('active');
      mobileMenu.classList.remove('active');
      overlay.classList.remove('active');
      mobileMenuBtn.setAttribute('aria-expanded', 'false');
      mobileMenu.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('aria-hidden', 'true');
    });
  }

  /* --------------------------------------------------------------------------
   * 2. Mega Menu - Keyboard Accessibility
   * ----------------------------------------------------------------------- */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Close any open mega menu by removing focus from trigger
      const activeMegaTrigger = document.querySelector(
        '.mega-menu-trigger:focus, .mega-menu-trigger:focus-within'
      );
      if (activeMegaTrigger) {
        activeMegaTrigger.blur();
      }

      // Also close mega menus that are shown via active class
      const activeMegaMenus = document.querySelectorAll('.mega-menu.active');
      activeMegaMenus.forEach((menu) => menu.classList.remove('active'));

      // Close mobile menu if open
      if (mobileMenu && mobileMenu.classList.contains('active')) {
        mobileMenuBtn.classList.remove('active');
        mobileMenu.classList.remove('active');
        if (overlay) overlay.classList.remove('active');
        mobileMenuBtn.setAttribute('aria-expanded', 'false');
        mobileMenu.setAttribute('aria-hidden', 'true');
        if (overlay) overlay.setAttribute('aria-hidden', 'true');
      }

      // Close quick view modal
      const quickViewModal = document.querySelector('.quick-view-modal');
      if (quickViewModal && quickViewModal.classList.contains('active')) {
        quickViewModal.classList.remove('active');
        if (overlay) overlay.classList.remove('active');
      }

      // Close cart drawer
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        cartDrawer.classList.remove('active');
        if (overlay) overlay.classList.remove('active');
        document.body.style.overflow = '';
        document.dispatchEvent(new CustomEvent('cart-drawer:close'));
      }

      // Close search overlay
      const searchOvl = document.getElementById('search-overlay');
      if (searchOvl && searchOvl.classList.contains('active')) {
        searchOvl.classList.remove('active');
        searchOvl.setAttribute('aria-hidden', 'true');
      }
    }
  });

  /* --------------------------------------------------------------------------
   * 3. Quantity Selectors (Event Delegation)
   * ----------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.quantity-btn');
    if (!btn) return;

    const wrapper = btn.parentElement;
    if (!wrapper) return;

    const input = wrapper.querySelector('.quantity-input');
    if (!input) return;

    let value = parseInt(input.value, 10) || 1;
    const buttons = wrapper.querySelectorAll('.quantity-btn');
    const isDecrease = btn === buttons[0];
    const isIncrease = btn === buttons[buttons.length - 1];

    if (isDecrease) {
      value = Math.max(1, value - 1);
    } else if (isIncrease) {
      value += 1;
    }

    input.value = value;

    // Dispatch an input event so other listeners can react
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Trigger cart update via cart drawer or direct AJAX fallback
    const lineAttr = input.getAttribute('data-line');
    const keyAttr = input.getAttribute('data-key') || btn.getAttribute('data-key');
    if (lineAttr && keyAttr) {
      if (window.cartDrawer && typeof window.cartDrawer.updateItem === 'function') {
        var result = window.cartDrawer.updateItem(keyAttr, value);
        if (result && typeof result.then === 'function') {
          result.then(function() {
            if (!input.closest('.cart-drawer')) {
              window.location.reload();
            }
          });
        }
      } else if (!input.closest('.cart-drawer')) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: keyAttr, quantity: value })
        }).then(function() {
          window.location.reload();
        });
      }
    }
  });

  /* --------------------------------------------------------------------------
   * 4. Tab Switching
   * ----------------------------------------------------------------------- */
  const tabButtons = document.querySelectorAll('.tab-btn');

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabId = btn.getAttribute('data-tab');
      if (!tabId) return;

      // Remove active from all tab buttons and content panels
      document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
      document.querySelectorAll('.tab-content').forEach((c) => c.classList.remove('active'));

      // Activate clicked button and matching content
      btn.classList.add('active');
      const targetContent = document.getElementById(tabId);
      if (targetContent) {
        targetContent.classList.add('active');
      }
    });
  });

  /* --------------------------------------------------------------------------
   * 5. Product Gallery Thumbnails
   * (Handled by product.js on product pages to avoid duplicate handlers)
   * ----------------------------------------------------------------------- */

  /* --------------------------------------------------------------------------
   * 6. Variant Option Buttons (Event Delegation)
   * ----------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    // Standard option buttons
    const optionBtn = e.target.closest('.option-btn:not(.disabled)');
    if (optionBtn) {
      const parent = optionBtn.closest('.option-buttons');
      if (parent) {
        parent.querySelectorAll('.option-btn').forEach((b) => b.classList.remove('active'));
        optionBtn.classList.add('active');
      }
      return;
    }

    // Color swatch buttons
    const colorBtn = e.target.closest('.color-btn:not(.disabled)');
    if (colorBtn) {
      const parent = colorBtn.closest('.color-options');
      if (parent) {
        parent.querySelectorAll('.color-btn').forEach((b) => b.classList.remove('active'));
        colorBtn.classList.add('active');
      }
    }
  });

  /* --------------------------------------------------------------------------
   * 7. Modal Open/Close
   * ----------------------------------------------------------------------- */
  const quickViewModal = document.querySelector('.quick-view-modal');

  // Close quick view via close button
  document.addEventListener('click', (e) => {
    if (e.target.closest('.quick-view-close')) {
      if (quickViewModal) quickViewModal.classList.remove('active');
      if (overlay) overlay.classList.remove('active');
    }
  });

  // Overlay click: close quick view modal and cart drawer
  if (overlay) {
    overlay.addEventListener('click', () => {
      if (quickViewModal && quickViewModal.classList.contains('active')) {
        quickViewModal.classList.remove('active');
        overlay.classList.remove('active');
      }

      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer && cartDrawer.classList.contains('active')) {
        cartDrawer.classList.remove('active');
        overlay.classList.remove('active');
        document.body.style.overflow = '';
        document.dispatchEvent(new CustomEvent('cart-drawer:close'));
      }
    });
  }

  /* --------------------------------------------------------------------------
   * 8. Announcement Bar Close
   * ----------------------------------------------------------------------- */
  const announcementClose = document.querySelector('.announcement-bar-close');
  const announcementBar = announcementClose
    ? announcementClose.closest('.announcement-bar')
    : null;

  if (announcementClose && announcementBar) {
    // Check if previously dismissed this session
    if (sessionStorage.getItem('flarehair-announcement-dismissed') === 'true') {
      announcementBar.style.display = 'none';
    }

    announcementClose.addEventListener('click', () => {
      announcementBar.style.display = 'none';
      sessionStorage.setItem('flarehair-announcement-dismissed', 'true');
    });
  }

  /* --------------------------------------------------------------------------
   * 9. Sticky Header Shadow
   * ----------------------------------------------------------------------- */
  const header = document.querySelector('.header');

  if (header) {
    const onScroll = () => {
      if (window.scrollY > 0) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    };

    // Set initial state
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --------------------------------------------------------------------------
   * 10. Search Overlay Toggle
   * ----------------------------------------------------------------------- */
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchOverlay = document.getElementById('search-overlay');

  if (searchToggle && searchOverlay) {
    searchToggle.addEventListener('click', () => {
      searchOverlay.classList.add('active');
      searchOverlay.setAttribute('aria-hidden', 'false');
      const searchInput = searchOverlay.querySelector('.search-overlay-input');
      if (searchInput) searchInput.focus();
    });

    const searchClose = searchOverlay.querySelector('.search-overlay-close');
    if (searchClose) {
      searchClose.addEventListener('click', () => {
        searchOverlay.classList.remove('active');
        searchOverlay.setAttribute('aria-hidden', 'true');
      });
    }
  }
});
